import { Button } from "@/components/bnp-ui";
import {
  AdminReviewStatus,
  BusinessDocument,
  BusinessDocumentType,
  DocumentStatus,
} from "@/types/business";
import {
  IconArrowRight,
  IconCheck,
  IconFileText,
  IconShieldCheck,
} from "@tabler/icons-react";
import DocumentStatusBadge from "./DocumentStatusBadge";
import { StagedDocument } from "./constants";
import { useMemo } from "react";

interface DocumentUploadCardsProps {
  documents: BusinessDocument[];
  stagedDocuments: StagedDocument[];
  isVerified: boolean;
  isRealtor: boolean;
  isDeveloper: boolean;
  onUploadType: (type: string) => void;
  onEdit: (doc: BusinessDocument) => void;
  onDelete: (doc: BusinessDocument) => void;
  onRemoveStaged: (type: BusinessDocumentType) => void;
  onSubmit: () => void;
  isSubmitPending: boolean;
}

const DOCUMENT_CARDS = [
  {
    key: "nin",
    type: "NIN",
    title: "NIN",
    description: "National Identification Number (NIN)",
  },
  {
    key: "cac",
    type: "CAC",
    title: "CAC",
    description: "Corporate Affairs Commission (CAC) certificate",
  },
];

// Builds a status-aware message for a realtor, based on the main document
// (engine) status and the admin review status.
const getRealtorStatusInfo = (
  doc: BusinessDocument,
): { message: string; dotClass: string; boxClass: string } => {
  const engineApproved = doc.status === DocumentStatus.VERIFIED;
  const engineRejected = doc.status === DocumentStatus.REJECTED;
  const adminApproved = doc.admin_review_status === AdminReviewStatus.APPROVED;
  const adminRejected = doc.admin_review_status === AdminReviewStatus.REJECTED;

  if (engineApproved) {
    return {
      message:
        "Your KYC verification is complete. Your account is fully verified.",
      dotClass: "bg-emerald-500",
      boxClass: "bg-emerald-50 border-emerald-200 text-emerald-700",
    };
  }
  if (engineRejected || adminRejected) {
    return {
      message:
        "Your document was rejected. Please re-upload a valid NIN or CAC document to complete your KYC verification.",
      dotClass: "bg-rose-500",
      boxClass: "bg-rose-50 border-rose-200 text-rose-700",
    };
  }
  if (adminApproved) {
    return {
      message:
        "Your document is being processed. We'll confirm your KYC verification once the review is complete.",
      dotClass: "bg-blue-500",
      boxClass: "bg-blue-50 border-blue-200 text-blue-700",
    };
  }
  return {
    message:
      "Your document has been submitted and is awaiting verification. No further uploads are required.",
    dotClass: "bg-amber-500",
    boxClass: "bg-amber-50 border-amber-200 text-amber-700",
  };
};

const UploadedDocumentCard = ({
  card,
  doc,
  readOnly,
  onEdit,
  onDelete,
}: {
  card: (typeof DOCUMENT_CARDS)[number];
  doc: BusinessDocument;
  readOnly: boolean;
  onEdit: (doc: BusinessDocument) => void;
  onDelete: (doc: BusinessDocument) => void;
}) => {
  const isEngineApproved = doc.status === DocumentStatus.VERIFIED;
  const isEngineRejected = doc.status === DocumentStatus.REJECTED;
  const isAdminRejected =
    doc.admin_review_status === AdminReviewStatus.REJECTED;
  return (
    <div
      className={`relative rounded-2xl p-5 border transition-all flex flex-col justify-between ${
        isEngineRejected || isAdminRejected
          ? "border-rose-200 bg-rose-50/30"
          : isEngineApproved
            ? "border-emerald-200 bg-emerald-50/20"
            : "border-slate-200 bg-white"
      }`}
    >
      <div>
        {/* Top Row: Icon + Status */}
        <div className="flex items-start justify-between gap-3">
          <div
            className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
              isEngineRejected || isAdminRejected
                ? "bg-rose-100 text-rose-600"
                : isEngineApproved
                  ? "bg-emerald-100 text-emerald-600"
                  : "bg-[#153e46]/10 text-[#153e46]"
            }`}
          >
            <IconCheck className="w-5 h-5" />
          </div>
          <DocumentStatusBadge doc={doc} />
        </div>

        {/* Title & Metadata */}
        <div className="mt-3.5 space-y-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-slate-900 text-sm font-bold">{card.title}</h3>
            {doc?.identification_number && (
              <span className="text-[11px] font-mono font-medium text-slate-500 bg-slate-100/80 px-2 py-0.5 rounded-md border border-slate-200/50">
                ID: {doc.identification_number}
              </span>
            )}
          </div>

          {doc?.upload_url && (
            <a
              href={doc.upload_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs font-semibold text-[#153e46] hover:underline pt-0.5"
            >
              View document file →
            </a>
          )}
        </div>

        {/* Rejection Banners */}
        {isAdminRejected && !isEngineApproved && (
          <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-200/80 text-xs text-amber-900 flex flex-col gap-0.5 mt-4">
            <p className="font-semibold text-amber-950">
              You need to re-upload this document
            </p>
            {doc.rejection_reason && (
              <p className="text-amber-800/90 text-[11px]">
                Reason: {doc.rejection_reason}
              </p>
            )}
          </div>
        )}

        {isEngineRejected && (
          <div className="p-3 rounded-xl bg-rose-500/10 border border-rose-200/80 text-xs text-rose-900 flex flex-col gap-0.5 mt-4">
            <p className="font-semibold text-rose-950">
              Verification Failed — Please re-submit
            </p>
            {doc.rejection_reason && (
              <p className="text-rose-800/90 text-[11px]">
                Reason: {doc.rejection_reason}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Actions Toolbar */}
      {!readOnly && (
        <div className="flex items-center gap-2 w-full pt-4 mt-4 border-t border-slate-100">
          {!isEngineApproved && (
            <button
              type="button"
              onClick={() => onEdit(doc)}
              className="flex-1 h-12 inline-flex items-center justify-center gap-1.5 rounded-lg bg-white border border-slate-200 text-xs font-semibold text-slate-700 hover:bg-slate-50 hover:text-slate-900 transition-colors shadow-xs"
            >
              Edit
            </button>
          )}
          <button
            type="button"
            onClick={() => onDelete(doc)}
            className={`inline-flex items-center justify-center gap-1.5 h-12 rounded-lg bg-rose-50 text-xs font-semibold text-rose-600 hover:bg-rose-100 border border-rose-100/80 transition-colors ${
              !isEngineApproved ? "flex-1" : "w-full"
            }`}
            title="Delete document"
          >
            Delete
          </button>
        </div>
      )}
    </div>
  );
};

const StagedDocumentCard = ({
  card,
  staged,
  onRemoveStaged,
}: {
  card: (typeof DOCUMENT_CARDS)[number];
  staged: StagedDocument;
  onRemoveStaged: (type: BusinessDocumentType) => void;
}) => {
  return (
    <div className="relative rounded-2xl p-5 border border-sky-200 bg-sky-50/40 transition-all flex flex-col justify-between">
      <div>
        {/* Top Row */}
        <div className="flex items-start justify-between gap-3">
          <div className="w-10 h-10 rounded-xl bg-sky-100 text-sky-600 flex items-center justify-center shrink-0">
            <IconCheck className="w-5 h-5" />
          </div>
          <span className="shrink-0 px-3 py-1 rounded-full text-[11px] font-bold tracking-wide uppercase border border-sky-200 bg-sky-50 text-sky-700">
            Ready to submit
          </span>
        </div>

        {/* Title & Metadata */}
        <div className="mt-3.5 space-y-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-slate-900 text-sm font-bold">{card.title}</h3>
            {staged?.number && (
              <span className="text-[11px] font-mono font-medium text-slate-500 bg-slate-100/80 px-2 py-0.5 rounded-md border border-slate-200/50">
                ID: {staged.number}
              </span>
            )}
          </div>

          {staged?.upload_url && (
            <a
              href={staged.upload_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs font-semibold text-[#153e46] hover:underline pt-0.5"
            >
              View document file →
            </a>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 w-full pt-4 mt-4 border-t border-slate-100">
        <button
          type="button"
          onClick={() => onRemoveStaged(staged.type)}
          className="flex-1 h-12 inline-flex items-center justify-center gap-1.5 rounded-lg bg-rose-50 text-xs font-semibold text-rose-600 hover:bg-rose-100 border border-rose-100/80 transition-colors"
        >
          Remove
        </button>
      </div>
    </div>
  );
};

const DisabledSlotCard = ({
  card,
}: {
  card: (typeof DOCUMENT_CARDS)[number];
}) => (
  <div className="relative rounded-2xl border border-dashed border-slate-200 bg-slate-50/70 p-4 opacity-75 select-none">
    <div className="flex items-center justify-between mb-3">
      <div className="w-9 h-9 rounded-xl bg-slate-200/60 flex items-center justify-center text-slate-400">
        <IconFileText className="w-4 h-4" />
      </div>
      <span className="text-[14px] font-medium text-slate-500 bg-slate-200/50 px-2 py-0.5 rounded">
        ---
      </span>
    </div>
    <h3 className="text-sm font-bold text-slate-500">{card.title}</h3>
    <p className="text-xs text-slate-400 mt-1 leading-relaxed">
      {card.description}
    </p>
  </div>
);

const UploadSlotButton = ({
  card,
  onUploadType,
}: {
  card: (typeof DOCUMENT_CARDS)[number];
  onUploadType: (type: string) => void;
}) => (
  <button
    type="button"
    onClick={() => onUploadType(card.type)}
    className="group relative rounded-2xl border border-slate-200 bg-white p-4 text-left transition-all hover:border-[#153e46] hover:shadow-md cursor-pointer flex flex-col justify-between"
  >
    <div>
      <div className="flex items-start justify-between">
        <div className="w-9 h-9 rounded-xl bg-[#153e46]/10 flex items-center justify-center text-[#153e46] transition-transform group-hover:scale-105">
          <IconFileText className="w-4 h-4" />
        </div>
        <div className="w-7 h-7 rounded-full bg-slate-100 group-hover:bg-[#153e46] flex items-center justify-center text-slate-400 group-hover:text-white transition-colors">
          <IconArrowRight className="w-3.5 h-3.5" />
        </div>
      </div>
      <h3 className="text-sm font-bold text-slate-900 mt-3 group-hover:text-[#153e46] transition-colors">
        Upload {card.title}
      </h3>
      <p className="text-xs text-slate-500 mt-1 leading-relaxed">
        {card.description}
      </p>
    </div>
  </button>
);

const VerifiedDocumentsList = ({
  documents,
}: {
  documents: BusinessDocument[];
}) => {
  const documentsByType = useMemo(() => {
    const map: Record<string, BusinessDocument> = {};
    documents.forEach((doc) => {
      if (doc.type) map[String(doc.type).toLowerCase()] = doc;
    });
    return map;
  }, [documents]);

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-3 p-4 rounded-2xl bg-emerald-50 border border-emerald-200">
        <div className="w-9 h-9 rounded-xl bg-emerald-100 text-emerald-600 flex items-center justify-center shrink-0">
          <IconShieldCheck size={18} />
        </div>
        <div>
          <h2 className="text-slate-900 font-bold text-sm">Verified Account</h2>
          <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">
            Your account is fully verified. Here are the documents on file.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {DOCUMENT_CARDS.map((card) => {
          const doc = documentsByType[card.key];
          if (!doc) return null;
          return (
            <UploadedDocumentCard
              key={card.key}
              card={card}
              doc={doc}
              readOnly
              onEdit={() => undefined}
              onDelete={() => undefined}
            />
          );
        })}
      </div>

      {documents.length === 0 && (
        <div className="text-center py-8 border border-dashed border-slate-200 rounded-2xl bg-slate-50/60">
          <p className="text-sm font-semibold text-slate-500">
            No uploaded documents
          </p>
        </div>
      )}
    </div>
  );
};

const DocumentUploadCards = ({
  documents,
  stagedDocuments,
  isVerified,
  isRealtor,
  isDeveloper,
  onUploadType,
  onEdit,
  onDelete,
  onRemoveStaged,
  onSubmit,
  isSubmitPending,
}: DocumentUploadCardsProps) => {
  const serverByType = useMemo(() => {
    const map: Record<string, BusinessDocument> = {};
    documents.forEach((doc) => {
      if (doc.type) map[String(doc.type).toLowerCase()] = doc;
    });
    return map;
  }, [documents]);

  const stagedByType = useMemo(() => {
    const map: Record<string, StagedDocument> = {};
    stagedDocuments.forEach((doc) => {
      map[String(doc.type).toLowerCase()] = doc;
    });
    return map;
  }, [stagedDocuments]);

  if (isVerified) {
    return <VerifiedDocumentsList documents={documents} />;
  }

  const requiredCount = isRealtor ? 1 : 2;
  const presentTypes = new Set([
    ...Object.keys(serverByType),
    ...Object.keys(stagedByType),
  ]);
  const remainingCount = Math.max(0, requiredCount - presentTypes.size);
  const hasUploadedAny = presentTypes.size > 0;

  // Realtors only need one document: once one is present, hide the other slot
  // so the uploaded document spans the full width.
  const visibleCards = DOCUMENT_CARDS.filter((card) => {
    if (!isRealtor || isDeveloper) return true;
    if (!hasUploadedAny) return true;
    return Boolean(serverByType[card.key] || stagedByType[card.key]);
  });

  let canSubmit = false;
  if (stagedDocuments.length > 0) {
    if (isDeveloper) {
      canSubmit = DOCUMENT_CARDS.every(
        (c) => serverByType[c.key] || stagedByType[c.key],
      );
    } else {
      // Realtor: one document (server or staged) satisfies the requirement.
      // A staged doc only exists when nothing is uploaded to the server yet.
      canSubmit = !Object.keys(serverByType).length;
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-slate-900 font-bold text-base">
            Verification Documents
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Provide your identity or business registration documents for
            verification.
          </p>
        </div>

        {remainingCount > 0 && (
          <div className="max-w-sm">
            <p className="text-xs font-semibold text-[#153e46] leading-relaxed">
              {isDeveloper
                ? "You need to upload both your NIN and CAC certificate for your KYC verification."
                : "You need to upload either your NIN or your CAC certificate for your KYC verification."}
            </p>
            <p className="text-[11px] text-slate-500 mt-1">
              {remainingCount} more document
              {remainingCount === 1 ? "" : "s"} left to add
            </p>
          </div>
        )}
      </div>

      <div
        className={`grid grid-cols-1 gap-4 ${
          visibleCards.length > 1 ? "md:grid-cols-2" : ""
        }`}
      >
        {visibleCards.map((card) => {
          const serverDoc = serverByType[card.key];
          const stagedDoc = stagedByType[card.key];

          if (serverDoc) {
            return (
              <UploadedDocumentCard
                key={card.key}
                card={card}
                doc={serverDoc}
                readOnly={false}
                onEdit={onEdit}
                onDelete={onDelete}
              />
            );
          }

          if (stagedDoc) {
            return (
              <StagedDocumentCard
                key={card.key}
                card={card}
                staged={stagedDoc}
                onRemoveStaged={onRemoveStaged}
              />
            );
          }

          // Realtors can still choose which single document to upload while
          // nothing is present; otherwise the slot is disabled.
          const canUpload = isDeveloper
            ? true
            : isRealtor
              ? !hasUploadedAny
              : false;

          if (!canUpload) {
            return <DisabledSlotCard key={card.key} card={card} />;
          }

          return (
            <UploadSlotButton
              key={card.key}
              card={card}
              onUploadType={onUploadType}
            />
          );
        })}
      </div>

      {isRealtor &&
        (() => {
          const realtorDoc = DOCUMENT_CARDS.map(
            (card) => serverByType[card.key],
          ).find(Boolean);
          if (!realtorDoc) return null;
          const info = getRealtorStatusInfo(realtorDoc);
          return (
            <div
              className={`flex items-center gap-2.5 text-xs border px-4 py-3 rounded-xl ${info.boxClass}`}
            >
              <span
                className={`w-1.5 h-1.5 rounded-full shrink-0 ${info.dotClass}`}
              />
              <p className="leading-relaxed">{info.message}</p>
            </div>
          );
        })()}

      {stagedDocuments.length > 0 && (
        <div className="flex flex-col sm:flex-row sm:items-center gap-4 p-4 rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-slate-700">
              {canSubmit
                ? isDeveloper
                  ? "Both NIN and CAC are ready. Submit them together."
                  : "Your document is ready. Submit it for verification."
                : "Submit button unlocks once all required documents are added."}
            </p>
            <p className="text-[11px] text-slate-400 mt-0.5">
              {canSubmit
                ? "Click submit to send your documents for KYC verification."
                : `${Math.max(
                    remainingCount,
                    0,
                  )} more required document${Math.max(remainingCount, 0) === 1 ? "" : "s"} still needed.`}
            </p>
          </div>
          <Button
            className="h-12 px-6 bg-[#153e46] hover:bg-[#0f2e34] text-white text-sm font-semibold rounded-xl shadow-md shadow-[#153e46]/20 transition-all shrink-0"
            onClick={onSubmit}
            disabled={!canSubmit || isSubmitPending}
            isLoading={isSubmitPending}
          >
            Submit KYC documents
          </Button>
        </div>
      )}
    </div>
  );
};

export default DocumentUploadCards;
